import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import PageLayout from "@layouts/PageLayout";
import { checkVesselDeficiencies, scanNomination } from "@services/vesselNominationApi";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { usePreNominationRequestFormStore } from "../pre-nomination-request/store/usePreNominationRequestForm.store";
import ErrorMessage from "./ErrorMessage";
import { NOMINATION_TYPE } from "./nominationTypeOptions";
import { getPostScanTarget } from "./postScanNavigation";
import type {
  VesselNominationScanItem,
  VesselNominationScanResponse,
  VesselNominationScanResultsProps,
} from "./VesselNominationScanResults.types";

const LANG_BASE = "vessel-nomination.vessel-scan.results";

const LOCAL_STORAGE_PAGE_KEY = "vessel-scan-results-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "vessel-scan-results-page-size";
const SCAN_RESULTS_INQUIRY_ID = "vessel-nomination.scan-results.page";

const VesselNominationScanResults: React.FC<Partial<VesselNominationScanResultsProps>> = ({
  testId = "vessel-nomination-scan-results",
}) => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { notify } = useNotification();
  const { setField } = usePreNominationRequestFormStore();
  const locationState = location.state as
    | {
        partialName?: string;
        nominationType?: string;
        pagination?: { total: number; pages: number; pageSize: number; pageNumber: number };
      }
    | undefined;

  const partialName = locationState?.partialName;
  const nominationType = locationState?.nominationType;
  const [hasDeficiency, setHasDeficiency] = useState(false);

  useEffect(() => {
    if (!partialName || !nominationType) {
      navigate("/vessel-nomination/vessel-scan", { replace: true });
    }
  }, [partialName, nominationType, navigate]);

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? Number.parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? Number.parseInt(saved, 10) : 25;
  });

  useEffect(() => {
    if (partialName && nominationType && page !== 1) {
      setPage(1);
    }
  }, [partialName, nominationType]);

  const canScan = Boolean(partialName && nominationType);

  const {
    data: apiData,
    isFetching,
    isError,
    isSuccess,
    dataUpdatedAt,
    error,
  } = useQuery<VesselNominationScanResponse>({
    enabled: canScan,
    queryKey: ["vessel-scan-results", partialName, nominationType, page, pageSize],
    queryFn: () => {
      if (!partialName || !nominationType) {
        throw new Error("Missing vessel scan parameters");
      }

      return scanNomination({
        imoOrName: partialName,
        nominationType,
        pageNumber: page - 1,
        pageSize,
      });
    },
    placeholderData: (prev) => prev,
  });

  useTrackPendingInquiry(SCAN_RESULTS_INQUIRY_ID, {
    isFetching,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const pageChangeRef = useRef(false);
  useEffect(() => {
    if (isFetching) return;
    pageChangeRef.current = false;
  }, [isFetching, apiData]);

  const vessels: VesselNominationScanItem[] = apiData?.vessels ?? [];
  const totalCount = apiData?.totalRecordCount ?? 0;

  const isInitialMountLoading = isFetching && !apiData;
  const showLoadingPlaceholder = isInitialMountLoading || (isFetching && pageChangeRef.current);

  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const handleSelect = useCallback((id: string | number) => setSelectedId(id), []);

  useEffect(() => {
    if (selectedId && !vessels.some((v) => v.id === selectedId)) {
      setSelectedId(null);
    }
  }, [vessels, selectedId]);

  const headers: Header[] = useMemo(
    () => [
      { field: "__select", columnName: "", width: 44, disableSort: true, render: () => null },
      { field: "vesselName", columnName: t(`${LANG_BASE}.tableHeaders.name`) },
      {
        field: "vesselImoNumber",
        columnName: t(`${LANG_BASE}.tableHeaders.imo`),
      },
      {
        field: "portName",
        columnName: t(`${LANG_BASE}.tableHeaders.port`),
      },
      {
        field: "shipBuildYear",
        columnName: t(`${LANG_BASE}.tableHeaders.year`),
      },
      {
        field: "countryCode",
        columnName: t(`${LANG_BASE}.tableHeaders.flag`),
      },
      {
        field: "shipsOwner",
        columnName: t(`${LANG_BASE}.tableHeaders.owner`),
      },
    ],
    [t],
  );

  const rows = useMemo(
    () =>
      vessels.map((v) => ({
        ...v,
        __select: v.id,
        __isSelected: v.id === selectedId,
        __onSelect: () => handleSelect(v.id),
      })),
    [vessels, selectedId, handleSelect],
  );

  if (!partialName || !nominationType) {
    return null;
  }

  const selectedVessel = selectedId ? (vessels.find((v) => v.id === selectedId) ?? null) : null;

  const handleProceed = () => {
    if (!selectedVessel) {
      notify({
        title: t(`${LANG_BASE}.select-vessel-warning-title`),
        description: t(`${LANG_BASE}.select-vessel-warning-description`),
        type: "error",
      });
    } else {
      checkVesselDeficiencies(selectedVessel.id)
        .then(() => {
          notify({
            title: t("vessel-nomination.vessel-scan.success"),
            type: "success",
          });
          if (nominationType !== NOMINATION_TYPE.DRY_BULK) {
            setField("vesselId", selectedVessel.id);
          }
          const target = getPostScanTarget(nominationType, selectedVessel);
          navigate(target.path, { state: target.state });
        })
        .catch((error) => {
          if (error.response?.status === 409) {
            setHasDeficiency(true);
            return;
          }
          notify({
            title: t("vessel-nomination.vessel-scan.scan-error-title"),
            description:
              (error.response?.data &&
              typeof error.response.data === "object" &&
              "detail" in error.response.data
                ? (error.response.data as { detail?: string }).detail
                : undefined) || t("vessel-nomination.vessel-scan.scan-error-description"),
            type: "error",
          });
          console.error("Vessel scan deficiency error:", error);
        });
    }
  };

  if (hasDeficiency) {
    return (
      <ErrorMessage
        errorMessageTitle={t("vessel-nomination.vessel-scan.has-deficiency-title")}
        errorMessageDescription={t("vessel-nomination.vessel-scan.has-deficiency-description")}
        confirmButtonLabel={t("vessel-nomination.vessel-scan.view-deficiencies")}
        onCancel={() => {
          setHasDeficiency(false);
        }}
        onConfirm={() => navigate(`/${paths.deficiencies.index}`)}
      />
    );
  }

  return (
    <PageLayout dir={dir}>
      <Subheader
        title={t("vessel-nomination.create-vessel-nomination")}
        subtitle={
          <span>
            <button
              onClick={() => navigate(`/${paths.vesselNomination.index}`)}
              className="cursor-pointer"
              style={{ color: theme.palette.text.brand }}
            >
              {t("navigation.vessel-nomination")}
            </button>{" "}
            / {t("vessel-nomination.create-vessel-nomination")}
          </span>
        }
      />
      <div className="pt-8 pb-8 px-24 flex flex-col gap-4">
        {error && (
          <div className="text-sm text-red-600">
            {error.message || t("common.error", { defaultValue: "Error" })}
          </div>
        )}

        {showLoadingPlaceholder ? (
          <div
            className="flex flex-col items-center justify-center py-20"
            data-testid="vessel-scan-results-loading-placeholder"
          >
            <RotateRightIcon
              fontSize="medium"
              sx={{
                "animation": "spin 0.9s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                "color": theme.palette.text.secondary,
              }}
              data-testid="vessel-scan-results-loading-spinner"
            />
          </div>
        ) : (
          <CustomDataTable
            testId={testId}
            title={t(`${LANG_BASE}.title`, { defaultValue: "Scan results" })}
            headers={headers}
            rows={rows}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            canPreview={false}
            pageSizeOptions={[15, 20, 25, 50]}
            onPageChange={(newPage, newSize) => {
              if (newPage !== page || newSize !== pageSize) {
                startInquiryTiming(SCAN_RESULTS_INQUIRY_ID, {
                  module:
                    nominationType === NOMINATION_TYPE.DRY_BULK
                      ? "dry_bulk_vessel_nomination"
                      : "vessel_nomination",
                  screen: "VesselNominationScanResults",
                  action: "scan_vessel",
                  entityType: "vessel",
                });
              }
              pageChangeRef.current = true;
              setPage(newPage);
              setPageSize(newSize);
              localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
              localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
            }}
            filters={[]}
            onApplyFilters={() => {}}
            onClearFilters={() => {}}
            hideFilters={true}
            availableFields={{}}
            actionName=""
            navigationLinkBase=""
          />
        )}
        <Stack
          direction="row"
          spacing={4}
          sx={{ mt: 2 }}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            variant="text"
            color="secondary"
            sx={{ color: theme.palette.primary.main, textTransform: "none" }}
            label={t("vessel-nomination.vessel-scan.scan-again")}
            onClick={() => navigate("/vessel-nomination/vessel-scan")}
            testId={`${testId}-scan-again-btn`}
          />
          <Button
            variant="contained"
            startIcon={<ContentPasteGoIcon fontSize="small" />}
            label={t(`${LANG_BASE}.proceed`)}
            sx={{ textTransform: "none" }}
            onClick={handleProceed}
            testId={`${testId}-proceed-btn`}
          />
        </Stack>
      </div>
    </PageLayout>
  );
};

export default VesselNominationScanResults;
